import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, X, Play, ExternalLink } from "lucide-react";
import { storageImage, storageSrcSet } from "@/lib/imageUrl";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { 
  parseGDriveUrl, 
  isVideoUrl, 
  getDriveThumbnailUrl, 
  getDrivePreviewUrl,
  getDriveViewUrl,
  getOptimalImageUrl
} from "@/lib/googleDriveHelpers";

interface ProjectGalleryProps {
  images: string[];
  projectTitle: string;
}

// Helper functions are now imported from googleDriveHelpers

const ProjectGallery = ({ images, projectTitle }: ProjectGalleryProps) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [retryCount, setRetryCount] = useState<{ [key: number]: number }>({});
  const { ref, isVisible } = useScrollAnimation();
  const scrollContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const animatedRowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [durations, setDurations] = useState<number[]>([]);
  const [videoFallbacks, setVideoFallbacks] = useState<Record<number, boolean>>({});

  // Split images into rows of exactly 15 (pad last row if needed)
  const rows: string[][] = [];
  for (let i = 0; i < images.length; i += 15) {
    rows.push(images.slice(i, i + 15));
  }
  
  // Pad the last row to have exactly 15 images for consistent animation speed
  const lastRow = rows[rows.length - 1];
  if (lastRow && lastRow.length < 15) {
    const needed = 15 - lastRow.length;
    for (let i = 0; i < needed; i++) {
      const fillImage = images[i % images.length];
      if (fillImage) lastRow.push(fillImage);
    }
  }

  // Normalize speed across rows by adjusting duration based on content width
  useEffect(() => {
    const pxPerSec = 30; // slower, consistent pixel speed across rows

    const compute = () => {
      const newDurations = rows.map((_, idx) => {
        const el = animatedRowRefs.current[idx];
        const width = el?.scrollWidth || 0;
        const distancePx = Math.max(1, width / 2); // we animate -50%
        const durationSec = distancePx / pxPerSec;
        return Math.max(30, Math.min(300, durationSec));
      });
      setDurations(newDurations);
    };

    // Initial and deferred computations to wait for image layout
    const raf = requestAnimationFrame(compute);
    const t1 = setTimeout(compute, 300);
    const t2 = setTimeout(compute, 1000);

    // Observe row size changes
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => compute());
      animatedRowRefs.current.forEach((el) => el && ro!.observe(el));
    }

    window.addEventListener('resize', compute);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', compute);
      if (ro) ro.disconnect();
    };
  }, [images.length]);

  const scroll = (rowIndex: number, direction: 'left' | 'right') => {
    const container = scrollContainerRefs.current[rowIndex];
    if (container) {
      const scrollAmount = 400;
      const targetScroll = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  const handleImageError = (index: number) => {
    console.error(`Failed to load image at index ${index}:`, images[index]);
    setFailedImages(prev => new Set(prev).add(index));
  };

  const handleRetry = (index: number) => {
    setFailedImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
    setRetryCount(prev => ({ ...prev, [index]: (prev[index] || 0) + 1 }));
  };

  const handlePrevImage = () => {
    if (selectedImageIndex === null) return;
    setSelectedImageIndex(selectedImageIndex === 0 ? images.length - 1 : selectedImageIndex - 1);
  };

  const handleNextImage = () => {
    if (selectedImageIndex === null) return;
    setSelectedImageIndex((selectedImageIndex + 1) % images.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrevImage();
    if (e.key === 'ArrowRight') handleNextImage();
    if (e.key === 'Escape') setSelectedImageIndex(null);
  };

  return (
    <>
      <div 
        ref={ref}
        className={`space-y-8 transition-all duration-1000 overflow-x-hidden max-w-full ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {rows.map((row, rowIndex) => {
          const isReverse = rowIndex % 2 === 1; // Alternate directions: 0->L, 1->R, 2->L, ...
          const duplicatedRow = [...row, ...row];
          const baseImageIndex = rowIndex * 15;

          return (
            <div key={rowIndex} className="relative">
              {/* Navigation Buttons */}
              <button
                onClick={() => scroll(rowIndex, 'left')}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-background/80 backdrop-blur-xs border border-border shadow-lg flex items-center justify-center hover:bg-background hover:border-primary/50 transition-all"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => scroll(rowIndex, 'right')}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-background/80 backdrop-blur-xs border border-border shadow-lg flex items-center justify-center hover:bg-background hover:border-primary/50 transition-all"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Gradient Overlays */}
              <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-background/80 md:from-background to-transparent z-[5] pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-background/80 md:from-background to-transparent z-[5] pointer-events-none" />

              {/* Scrolling Container */}
              <div 
                ref={(el) => { scrollContainerRefs.current[rowIndex] = el; }}
                className="overflow-x-auto scrollbar-hide scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div 
                  ref={(el) => { animatedRowRefs.current[rowIndex] = el; }}
                  className={`flex gap-6 hover:pause-animation`}
                  style={{
                    width: 'fit-content',
                    willChange: 'transform',
                    animation: `${isReverse ? 'scroll-reverse' : 'scroll'} ${(durations[rowIndex] || 100)}s linear infinite`
                  }}
                >
                  {duplicatedRow.map((image, imgIndex) => {
                    const originalIndex = (baseImageIndex + (imgIndex % row.length)) % images.length;
                    
                    // Skip rendering if image failed to load
                    if (failedImages.has(originalIndex)) {
                      return null;
                    }
                    
                    const isVideo = isVideoUrl(image);
                    const rawUrl = isVideo && image.startsWith('video::') ? image.replace('video::', '') : image;
                    const isDrive = rawUrl.includes('drive.google.com') || rawUrl.includes('gdrive::');
                    const { fileId, thumbnail } = parseGDriveUrl(rawUrl);
                    const retries = retryCount[originalIndex] || 0;
                    // Google Drive media is not shown on public pages (no request to Google before consent)
                    if (isDrive) return null;
                    
                    return (
                      <div
                        key={`${rowIndex}-${imgIndex}-${retries}`}
                        className="flex-shrink-0 w-[150px] md:w-[300px] lg:w-[400px] h-[110px] md:h-[225px] lg:h-[300px] cursor-pointer group"
                        onClick={() => setSelectedImageIndex(originalIndex)}
                      >
                        <div className="relative w-full h-full overflow-hidden rounded-xl border border-border bg-card transition-all duration-300 group-hover:border-primary/50 group-hover:shadow-xl group-hover:shadow-primary/20">
                          {isVideo ? (
                            isDrive && fileId ? (
                              <div className="w-full h-full relative">
                                <img
                                  src={thumbnail || getDriveThumbnailUrl(fileId, 'large')!}
                                  alt={`${projectTitle} – Video Vorschau ${originalIndex + 1}`}
                                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                  loading="lazy"
                                  onContextMenu={(e) => e.preventDefault()}
                                  draggable={false}
                                  onError={() => handleImageError(originalIndex)}
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="rounded-full bg-background/70 border border-border backdrop-blur-xs p-3 md:p-4">
                                    <Play className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <video
                                src={rawUrl}
                                className="w-full h-full object-cover"
                                muted
                                playsInline
                                preload="metadata"
                                onContextMenu={(e) => e.preventDefault()}
                                onError={() => handleImageError(originalIndex)}
                              />
                            )
                          ) : (
                            <img
                              src={storageImage(getOptimalImageUrl(image, 'thumbnail'), 640)}
                              srcSet={storageSrcSet(getOptimalImageUrl(image, 'thumbnail'), [400, 640, 800])}
                              sizes="(max-width: 768px) 150px, (max-width: 1024px) 300px, 400px"
                              alt={`${projectTitle} - Image ${originalIndex + 1}`}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                              loading="lazy"
                              decoding="async"
                              onContextMenu={(e) => e.preventDefault()}
                              draggable={false}
                              onError={(e) => {
                                // Fallback to thumbnail if available
                                if (thumbnail && (e.currentTarget as HTMLImageElement).src !== thumbnail) {
                                  (e.currentTarget as HTMLImageElement).src = thumbnail;
                                } else {
                                  handleImageError(originalIndex);
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={selectedImageIndex !== null} onOpenChange={(open) => !open && setSelectedImageIndex(null)}>
        <DialogContent 
          className="max-w-7xl w-full h-[90vh] p-0 bg-background/95 backdrop-blur-xl border-border"
          onKeyDown={handleKeyDown}
        >

          {selectedImageIndex !== null && (() => {
            const currentMedia = images[selectedImageIndex];
            if (!currentMedia) return null;
            const isVideo = isVideoUrl(currentMedia);
            const rawUrl = isVideo && currentMedia.startsWith('video::') ? currentMedia.replace('video::', '') : currentMedia;
            const isDrive = rawUrl.includes('drive.google.com') || rawUrl.includes('gdrive::');
            const { fileId, thumbnail } = parseGDriveUrl(rawUrl);
            const retries = retryCount[selectedImageIndex] || 0;
            const isFailed = failedImages.has(selectedImageIndex);
            if (isDrive) {
              return (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground p-8 text-center">
                  Dieses Video ist derzeit nicht verfügbar.
                </div>
              );
            }
            
            const videoSrc = isVideo && isDrive && fileId
              ? `https://buhajnrjzecedelnxllv.supabase.co/functions/v1/google-drive-stream?id=${fileId}&retry=${retries}`
              : rawUrl;

            return (
              <div className="relative w-full h-full flex items-center justify-center p-3 sm:p-5 md:p-8">
                {/* Standardized frame that fits the screen */}
                <div
                  className="relative w-[92vw] sm:w-[90vw] md:w-[85vw] lg:w-[80vw] max-w-5xl h-[70vh] sm:h-[72vh] md:h-[75vh] lg:h-[78vh] rounded-xl border border-border bg-black shadow-lg overflow-hidden"
                >
                  {isFailed ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-muted text-muted-foreground p-8">
                      {thumbnail && (
                        <img 
                          src={thumbnail} 
                          alt="Thumbnail"
                          className="w-48 h-48 object-cover mb-6 rounded-sm opacity-50"
                        />
                      )}
                      <p className="text-center mb-3 font-medium text-lg">Failed to load media</p>
                      <p className="text-sm text-center mb-6 max-w-md">
                        This file may be private or requires additional permissions. Try viewing it directly on Google Drive.
                      </p>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleRetry(selectedImageIndex)}
                          variant="secondary"
                        >
                          Retry
                        </Button>
                        {isDrive && fileId && (
                          <Button
                            onClick={() => window.open(getDriveViewUrl(fileId)!, '_blank')}
                            className="gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open in Drive
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : isVideo ? (
                    isDrive && fileId ? (
                      <div className="relative w-full h-full">
                        {videoFallbacks[selectedImageIndex] ? (
                          // Fallback to Google Drive iframe player
                          <div className="w-full h-full flex flex-col items-center justify-center bg-black p-4">
                            <iframe
                              src={getDrivePreviewUrl(fileId)!}
                              className="w-full h-[80%] rounded-sm"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                              allowFullScreen
                            />
                            <div className="mt-4 flex gap-3">
                              <Button
                                onClick={() => {
                                  setVideoFallbacks(prev => ({ ...prev, [selectedImageIndex]: false }));
                                  handleRetry(selectedImageIndex);
                                }}
                                variant="secondary"
                                size="sm"
                              >
                                Try Direct Stream Again
                              </Button>
                              <Button
                                onClick={() => window.open(getDriveViewUrl(fileId)!, '_blank')}
                                size="sm"
                                className="gap-2"
                              >
                                <ExternalLink className="w-4 h-4" />
                                Open in Drive
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <video
                            src={videoSrc}
                            className="absolute inset-0 w-full h-full object-contain"
                            controls
                            controlsList="nodownload"
                            preload="metadata"
                            autoPlay
                            key={`${selectedImageIndex}-${retries}`}
                            onContextMenu={(e) => e.preventDefault()}
                            onError={(e) => {
                              console.error('Video failed to load, switching to Drive player:', videoSrc);
                              setVideoFallbacks(prev => ({ ...prev, [selectedImageIndex]: true }));
                            }}
                          >
                            <source src={videoSrc} type="video/mp4" />
                            Ihr Browser unterstützt das Video-Tag nicht.
                          </video>
                        )}
                      </div>
                    ) : (
                      <video
                        src={videoSrc}
                        className="absolute inset-0 w-full h-full object-contain"
                        controls
                        controlsList="nodownload"
                        preload="metadata"
                        autoPlay
                        key={`${selectedImageIndex}-${retries}`}
                        onContextMenu={(e) => e.preventDefault()}
                        onLoadedMetadata={(e) => {
                          console.log('Video metadata loaded successfully (non-Drive):', videoSrc);
                          const video = e.currentTarget;
                          console.log('Video duration:', video.duration, 'seconds');
                        }}
                        onError={(e) => {
                          console.error('Video failed to load (non-Drive):', videoSrc);
                          console.error('Video error:', e.currentTarget.error);
                          handleImageError(selectedImageIndex);
                        }}
                      >
                        <source src={videoSrc} type="video/mp4" />
                        <source src={videoSrc} type="video/webm" />
                        <source src={videoSrc} type="video/quicktime" />
                        Ihr Browser unterstützt das Video-Tag nicht.
                      </video>
                    )
                  ) : (
                    <img
                      src={storageImage(getOptimalImageUrl(currentMedia, 'full'), 1600, { quality: 80 })}
                      srcSet={storageSrcSet(getOptimalImageUrl(currentMedia, 'full'), [1200, 1600, 2000], { quality: 80 })}
                      sizes="90vw"
                      alt={`${projectTitle} - Image ${selectedImageIndex + 1}`}
                      className="absolute inset-0 w-full h-full object-contain"
                      key={`${selectedImageIndex}-${retries}`}
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                      onError={(e) => {
                        // Fallback to thumbnail if available
                        if (thumbnail && (e.currentTarget as HTMLImageElement).src !== thumbnail) {
                          console.log("Falling back to thumbnail");
                          (e.currentTarget as HTMLImageElement).src = thumbnail;
                        } else {
                          handleImageError(selectedImageIndex);
                        }
                      }}
                    />
                  )}

                {/* Navigation Arrows - inside the frame */}
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-background/80 backdrop-blur-xs hover:bg-background hover:border-primary/50"
                  onClick={handlePrevImage}
                >
                  <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                </Button>

                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-background/80 backdrop-blur-xs hover:bg-background hover:border-primary/50"
                  onClick={handleNextImage}
                >
                  <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
                </Button>

                  {/* Media Counter */}
                  <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-background/80 backdrop-blur-xs border border-border text-xs sm:text-sm">
                    {selectedImageIndex + 1} / {images.length}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes scroll {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }

        @keyframes scroll-reverse {
          from {
            transform: translateX(-50%);
          }
          to {
            transform: translateX(0);
          }
        }

        .hover\\:pause-animation:hover {
          animation-play-state: paused !important;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default ProjectGallery;
